import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { postResetPassword } from "../../api/create";
import { IoEyeOffOutline, IoEyeOutline } from "react-icons/io5";
import * as yup from "yup";
import { FiTarget } from "react-icons/fi";
import { RxCheckCircled } from "react-icons/rx";

interface ChangePasswordFormValues {
  old_password: string;
  new_password: string;
  confirm_password: string;
}

const ChangePassword: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [, setLoader] = useState(false);
  const [passwordRules, setPasswordRules] = useState({
    hasNumber: false,
    hasCharacter: false,
    minLength: false
  });


  

  const validationSchema = yup.object({
    old_password: yup.string().required("Old password is required"),
    new_password: yup
      .string()
      .min(8, "Password must be at least 8 characters")
      .required("New password is required")
      .test('not-same-as-old', 'New password cannot be the same as the old password', function(value) {
        const { old_password } = this.parent;
        return value !== old_password; // Check if new password is the same as old password
      }),
    confirm_password: yup
      .string()
      .oneOf([yup.ref("new_password")], "The new password and the verified password must match")
      .required("Confirm password is required"),
  });

  const formik = useFormik<ChangePasswordFormValues>({
    initialValues: {
      old_password: "",
      new_password: "",
      confirm_password: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        // Replace with API call for changing password
        setLoader(true);
        await postResetPassword({
          old_password: values.old_password,
          new_password: values.new_password,
          confirm_password: values.confirm_password,
        })
          .then((res: any) => {
            
            if (res?.success) {
              toast.success("Password changed successfully");
            } else {
              toast.error(res?.detail?.message || "Password changed failed");
            }
            setLoader(false);
          })
          .catch((res?: any) => {
            toast.error(res?.message || "Failed to change password");
          });
      } catch (error) {
        toast.error("An error occurred while changing the password.");
      }
    },
  });



  useEffect(() => {
    // Track password rule validations dynamically as the user types
    const newPassword = formik.values.new_password;

    // Password rules
    setPasswordRules({
      hasNumber: /\d/.test(newPassword),
      hasCharacter: /[a-zA-Z]/.test(newPassword),
      minLength: newPassword.length >= 8,
    });
  }, [formik.values.new_password]);

  


  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 relative">
      {/* <AccountSettingsTitle
        title="Security settings"
        description="Update your password details here"
      /> */}


      <form onSubmit={formik.handleSubmit} className=" ">

      <div className='flex justify-between items-center mb-4'>
      <div>
          <h2 className="text-base font-semibold text-[#202A2B]">Security settings</h2>
          <p className="text-sm text-gray-600">Update your password.</p>
      </div>
      
      <button
          type="submit"
          disabled={formik.isSubmitting}
          className="px-4 py-2 text-sm font-medium text-white bg-[#17BE62] rounded-lg focus:outline-none focus:ring-4 focus:ring-blue-300 disabled:opacity-50"
      >
          {formik.isSubmitting ? 'Changing...' : 'Change Password'}
      </button>
      </div>


      <hr className="my-4 border-gray-300" />

      <div className='grid grid-cols-2 gap-4'>
        {/* Old Password */}
        <div className="relative">
          <label
            htmlFor="old_password"
            className="block text-sm font-medium text-gray-600"
          >
            Old Password
          </label>
          <input
            type={showOldPassword ? "text" : "password"}
            id="old_password"
            name="old_password"
            placeholder="Enter old password"
            value={formik.values.old_password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className={`w-full px-4 py-2 mt-2 text-sm text-gray-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              formik.touched.old_password && formik.errors.old_password
                ? "border-red-500"
                : "border-gray-300"
            }`}
          />
          <span
            onClick={() => setShowOldPassword(!showOldPassword)}
            className="cursor-pointer   absolute mt-4 right-4"
          >
            {showOldPassword ? <IoEyeOutline /> : <IoEyeOffOutline />}
          </span>



          {formik.touched.old_password && formik.errors.old_password ?(
            <div className="mt-1 text-sm text-red-500">
              {formik.errors.old_password}
            </div>
          ):<div className="mt-1 text-sm text-red-500"> &nbsp;</div>}


        </div>

        {/* New Password */}
        <div className="relative">
          <label
            htmlFor="new_password"
            className="block text-sm font-medium text-gray-600"
          >
            New Password
          </label>
          <input
            type={showPassword ? "text" : "password"}
            id="new_password"
            name="new_password"
            placeholder="Enter new password"
            value={formik.values.new_password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className={`w-full px-4 py-2 mt-2 text-sm text-gray-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              formik.touched.new_password && formik.errors.new_password
                ? "border-red-500"
                : "border-gray-300"
            }`}
          />

          <span
            onClick={() => setShowPassword(!showPassword)}
            className="cursor-pointer absolute mt-4 right-4"
          >
            {showPassword ? <IoEyeOutline /> : <IoEyeOffOutline />}
          </span>
          {formik.touched.new_password && formik.errors.new_password ? (
            <div className="mt-1 text-sm text-red-500">
              {formik.errors.new_password}
            </div>
          ):<div className="mt-1 text-sm text-red-500"> &nbsp;</div>}
        </div>

        {/* Confirm Password */}
        <div className="relative">
          <label
            htmlFor="confirm_password"
            className="block text-sm font-medium text-gray-600"
          >
            Confirm Password
          </label>
          <input
            type={showConfirmPassword ? "text" : "password"}
            id="confirm_password"
            name="confirm_password"
            placeholder="Confirm new password"
            value={formik.values.confirm_password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className={`w-full px-4 py-2 mt-2 text-sm text-gray-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              formik.touched.confirm_password && formik.errors.confirm_password
                ? "border-red-500"
                : "border-gray-300"
            }`}
          />
          <span
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="cursor-pointer absolute mt-4 right-4"
          >
            {showConfirmPassword ? <IoEyeOutline /> : <IoEyeOffOutline />}
          </span>
          {formik.touched.confirm_password &&
            formik.errors.confirm_password ? (
              <div className="mt-1 text-sm text-red-500">
                {formik.errors.confirm_password}
              </div>
            ):<div className="mt-1 text-sm text-red-500"> &nbsp;</div>}
        </div>
        
        </div>

        <hr className="my-4 border-gray-300" />

        <div className='!mt-0'>
          <ul className="mt-0  text-xs list-none">
            <li className={`mb-2 ${passwordRules.hasNumber ? 'text-green-500' : 'text-red-500'}`}>
              <span className='flex items-center'>{passwordRules.hasNumber ? <RxCheckCircled className='mr-1' /> : <FiTarget className='mr-1' />} Contains at least one number</span>
            </li>
            <li className={`mb-2 ${passwordRules.hasCharacter ? 'text-green-500' : 'text-red-500'}`}>
              <span className='flex items-center'>{passwordRules.hasCharacter ? <RxCheckCircled className='mr-1' /> : <FiTarget className='mr-1' />} Contains at least one letter</span>
            </li>
            <li className={`mb-1 ${passwordRules.minLength ? 'text-green-500' : 'text-red-500'}`}>
              <span className='flex items-center'>{passwordRules.minLength ? <RxCheckCircled className='mr-1' /> : <FiTarget className='mr-1' />} At least 8 characters</span>
            </li>
          </ul>
        </div>
      
      </form>
    </div>
  );
};

export default ChangePassword;
